<script setup>
import { ref, computed, watch } from 'vue'
import AppLayout from '@/layouts/AppLayout.vue'

const props = defineProps({
  equipement:    Object,
  estimation:    Object,
  rapports:      Array,
  totaux:        Object,
  can_see_costs: Boolean,
})

function fmtCurrency(val) {
  if (val === null || val === undefined) return '—'
  return new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 2 }).format(val)
}
function fmtQty(val) {
  if (val === null || val === undefined) return '—'
  return new Intl.NumberFormat('fr-CA', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(val)
}

const pageSize    = ref(25)
const currentPage = ref(1)
watch(pageSize, () => { currentPage.value = 1 })

const totalPages  = computed(() => Math.max(1, Math.ceil(props.rapports.length / pageSize.value)))
const paginated   = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value
  return props.rapports.slice(start, start + pageSize.value)
})
const pageNumbers = computed(() => {
  const pages = []
  for (let i = 1; i <= totalPages.value; i++) pages.push(i)
  return pages
})

function copyTable() {
  const header = ['Date', 'Utilisateur', 'Quantité', ...(props.can_see_costs ? ['Coûts'] : [])].join('\t')
  const rows = props.rapports.map(r =>
    [r.date, r.utilisateur, r.quantite, ...(props.can_see_costs ? [r.quantite_taux] : [])].join('\t')
  )
  navigator.clipboard.writeText([header, ...rows].join('\n'))
    .then(() => alert('Copié !'))
    .catch(() => alert('Impossible de copier'))
}

function printTable() { window.print() }

function exportCsv() {
  const sep = ';'
  const header = ['Date', 'Utilisateur', 'Quantité', ...(props.can_see_costs ? ['Coûts'] : [])].join(sep)
  const rows = props.rapports.map(r =>
    [r.date, r.utilisateur, r.quantite, ...(props.can_see_costs ? [r.quantite_taux] : [])].join(sep)
  )
  const blob = new Blob(['\uFEFF' + [header, ...rows].join('\n')], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = `rapports_${props.equipement.code}.csv`; a.click()
  URL.revokeObjectURL(url)
}
</script>

<template>
  <AppLayout>
    <template #topbar-left>
      <div class="flex items-center gap-3">
        <a :href="estimation.show_url" class="text-outline hover:text-on-surface transition-colors">
          <span class="material-symbols-outlined text-xl">arrow_back</span>
        </a>
        <span class="text-outline">/</span>
        <span class="text-sm font-black uppercase tracking-widest text-on-surface">
          {{ equipement.code }} — {{ equipement.nom }}
        </span>
      </div>
    </template>

    <div class="p-8 space-y-6">

      <!-- Sommaire -->
      <div class="bg-surface-container rounded-xl overflow-hidden shadow-xl border border-outline-variant/10">
        <div class="px-6 py-4 bg-surface-container-high/60 border-b border-outline-variant/15">
          <h2 class="text-[11px] font-black uppercase tracking-widest text-on-surface">Sommaire</h2>
        </div>
        <table class="w-full text-left border-collapse">
          <tbody class="divide-y divide-outline-variant/10">
            <tr class="hover:bg-surface-container-highest/20 transition-colors">
              <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-48">Estimation</th>
              <td class="px-6 py-3 font-mono text-sm font-bold text-primary">
                <a :href="estimation.show_url" class="hover:brightness-125 transition-colors">{{ estimation.code }}</a>
              </td>
            </tr>
            <tr class="hover:bg-surface-container-highest/20 transition-colors">
              <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-48">Heures</th>
              <td class="px-6 py-3 font-mono text-sm font-bold text-on-surface">{{ fmtQty(totaux.heures) }}</td>
            </tr>
            <tr v-if="can_see_costs" class="hover:bg-surface-container-highest/20 transition-colors">
              <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-48">Taux horaire</th>
              <td class="px-6 py-3 font-mono text-sm text-on-surface">{{ fmtCurrency(totaux.taux_horaire) }}</td>
            </tr>
            <tr v-if="can_see_costs" class="hover:bg-surface-container-highest/20 transition-colors">
              <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-48">Coûts totaux</th>
              <td class="px-6 py-3 font-mono text-sm font-bold text-primary">{{ fmtCurrency(totaux.couts) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Tableau des rapports -->
      <div class="bg-surface-container rounded-xl overflow-hidden shadow-xl border border-outline-variant/10">
        <div class="px-6 py-4 bg-surface-container-high/60 border-b border-outline-variant/15">
          <h2 class="text-[11px] font-black uppercase tracking-widest text-on-surface">
            Rapports ({{ rapports.length }})
          </h2>
        </div>
        <div class="overflow-x-auto">
          <table class="w-full text-left border-collapse text-sm">
            <thead>
              <tr class="border-b border-outline-variant/15">
                <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline">Date</th>
                <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline">Utilisateur</th>
                <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline text-right">Quantité</th>
                <th v-if="can_see_costs" class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline text-right">Coûts</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-outline-variant/10">
              <tr v-if="!rapports.length">
                <td :colspan="can_see_costs ? 4 : 3" class="px-6 py-12 text-center text-outline text-sm">Aucun rapport</td>
              </tr>
              <tr v-for="(r, i) in paginated" :key="i"
                  :class="['hover:bg-surface-container-highest/20 transition-colors', i % 2 === 1 ? 'bg-surface-container-low/20' : '']">
                <td class="px-6 py-3 font-mono text-xs whitespace-nowrap">
                  <a :href="r.rapport_url" class="text-primary hover:brightness-125 transition-colors">{{ r.date }}</a>
                </td>
                <td class="px-6 py-3 text-xs text-on-surface">{{ r.utilisateur }}</td>
                <td class="px-6 py-3 text-right font-mono text-xs text-on-surface">{{ fmtQty(r.quantite) }}</td>
                <td v-if="can_see_costs" class="px-6 py-3 text-right font-mono text-xs text-on-surface">{{ fmtCurrency(r.quantite_taux) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <!-- Pagination -->
        <div v-if="rapports.length" class="px-6 py-4 bg-surface-container-high/40 border-t border-outline-variant/15 flex justify-between items-center">
          <div class="text-[10px] text-outline font-black uppercase tracking-widest">
            Affichage de {{ (currentPage - 1) * pageSize + 1 }}
            à {{ Math.min(currentPage * pageSize, rapports.length) }}
            sur {{ rapports.length }} entrée{{ rapports.length > 1 ? 's' : '' }}
          </div>
          <div class="flex gap-1">
            <button :disabled="currentPage === 1"
              class="w-8 h-8 flex items-center justify-center rounded bg-surface-container border border-outline-variant/15 text-outline hover:text-primary disabled:opacity-30 transition-colors"
              @click="currentPage--">
              <span class="material-symbols-outlined text-base">chevron_left</span>
            </button>
            <template v-for="p in pageNumbers" :key="p">
              <button v-if="p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1"
                :class="['w-8 h-8 flex items-center justify-center rounded text-[10px] font-black transition-colors',
                  p === currentPage ? 'bg-primary text-on-primary' : 'bg-surface-container-low border border-outline-variant/10 text-outline hover:text-primary']"
                @click="currentPage = p">{{ p }}</button>
              <span v-else-if="p === 2 || p === totalPages - 1"
                class="w-8 h-8 flex items-center justify-center text-outline text-sm">…</span>
            </template>
            <button :disabled="currentPage === totalPages"
              class="w-8 h-8 flex items-center justify-center rounded bg-surface-container border border-outline-variant/15 text-outline hover:text-primary disabled:opacity-30 transition-colors"
              @click="currentPage++">
              <span class="material-symbols-outlined text-base">chevron_right</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Boutons export -->
      <div v-if="rapports.length" class="flex items-center gap-3 no-print">
        <button
          class="flex items-center gap-2 px-6 py-4 text-xs font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 rounded-md transition-all hover:brightness-110 active:scale-95"
          @click="exportCsv">
          <span class="material-symbols-outlined text-base">download</span>Excel / CSV
        </button>
        <button
          class="flex items-center gap-2 px-6 py-4 text-xs font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 rounded-md transition-all hover:brightness-110 active:scale-95"
          @click="copyTable">
          <span class="material-symbols-outlined text-base">content_copy</span>Copier
        </button>
        <button
          class="flex items-center gap-2 px-6 py-4 text-xs font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 rounded-md transition-all hover:brightness-110 active:scale-95"
          @click="printTable">
          <span class="material-symbols-outlined text-base">print</span>Imprimer
        </button>
      </div>

    </div>
  </AppLayout>
</template>

<style scoped>
@media print {
  .no-print { display: none !important; }
}
</style>
